import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Users, Calendar, Shield, BarChart2, ChevronRight,
  MapPin, Clock, ExternalLink, Loader2, Home
} from "lucide-react";
import axios from "axios";
import { MetaTags } from "./MetaTags";
import { 
  LeagueHomeSection, 
  StandingsSection, 
  TeamsSection, 
  GamesSection, 
  PlayoffsSection 
} from "./league-page";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const LeaguePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [league, setLeague] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if returning from a sub-page with a specific tab
  const returnToTab = location.state?.returnToTab;
  const [activeTab, setActiveTab] = useState(returnToTab || "home");

  // Update active tab if returning from a sub-page
  useEffect(() => {
    if (returnToTab) {
      setActiveTab(returnToTab);
    }
  }, [returnToTab]);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        setLoading(true);
        const [leagueRes, gamesRes] = await Promise.all([
          axios.get(`${API}/leagues/${slug}`),
          axios.get(`${API}/leagues/${slug}/games`)
        ]);
        setLeague(leagueRes.data);
        setGames(gamesRes.data);
      } catch (err) {
        console.error("Failed to load league:", err);
        setError("League not found");
      } finally {
        setLoading(false);
      }
    };
    fetchLeague();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4">
        <Trophy className="w-16 h-16 text-zinc-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">League Not Found</h1>
        <p className="text-zinc-400 mb-6">The league you are looking for does not exist or has been removed.</p>
        <Link to="/" className="text-orange-500 hover:text-orange-400">
          ← Back to Home
        </Link>
      </div>
    );
  }

  // Get ad banners by position
  const getAdBanners = (position) => {
    return (league.ad_banners || []).filter(b => b.is_active && b.position === position);
  };

  const topBanners = getAdBanners("top");
  const sidebarBanners = getAdBanners("sidebar");
  const betweenSectionBanners = getAdBanners("between_sections");

  // Prepare standings with team info
  const standingsWithTeams = (league.standings || []).map(s => ({
    ...s,
    team: league.teams?.find(t => t.id === s.team_id)
  })).sort((a, b) => {
    const aWinPct = a.wins / Math.max(a.wins + a.losses, 1);
    const bWinPct = b.wins / Math.max(b.wins + b.losses, 1);
    if (b.wins !== a.wins) return b.wins - a.wins;
    return bWinPct - aWinPct;
  });

  // Group standings by conference/division if exists
  const getGroupedStandings = () => {
    if (league.conferences?.length > 0) {
      const groups = league.conferences.map(conf => ({
        name: conf.name,
        color: conf.color,
        teams: standingsWithTeams.filter(s => s.team?.conference_id === conf.id)
      })).filter(g => g.teams.length > 0);
      
      // Add unassigned teams to a separate group
      const unassignedTeams = standingsWithTeams.filter(s => !s.team?.conference_id);
      if (unassignedTeams.length > 0) {
        groups.push({ name: "Standings", teams: unassignedTeams });
      }
      
      return groups.length > 0 ? groups : [{ name: "Standings", teams: standingsWithTeams }];
    }
    if (league.divisions?.length > 0) {
      const groups = league.divisions.map(div => ({
        name: div.name,
        color: div.color,
        teams: standingsWithTeams.filter(s => s.team?.division_id === div.id)
      })).filter(g => g.teams.length > 0);
      
      // Add unassigned teams to a separate group
      const unassignedTeams = standingsWithTeams.filter(s => !s.team?.division_id);
      if (unassignedTeams.length > 0) {
        groups.push({ name: "Standings", teams: unassignedTeams });
      }
      
      return groups.length > 0 ? groups : [{ name: "Standings", teams: standingsWithTeams }];
    }
    return [{ name: "Standings", teams: standingsWithTeams }];
  };

  // Separate games into upcoming and past
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const upcomingGames = games.filter(g => g.scheduled_date >= todayStr && !g.is_completed);
  const pastGames = games.filter(g => g.scheduled_date < todayStr || g.is_completed).reverse();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="league-page">
      <MetaTags
        title={league.seo?.meta_title || `${league.name} | Antler Sports Network`}
        description={league.seo?.meta_description || league.description || `Follow ${league.name} standings, schedule, and playoffs`}
        image={league.seo?.og_image || league.logo_url}
      />

      {/* Top Ad Banner */}
      {topBanners.length > 0 && (
        <div className="w-full bg-black border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 py-3">
            {topBanners.map(banner => (
              <AdBanner key={banner.id} banner={banner} />
            ))}
          </div>
        </div>
      )}

      {/* League Header */}
      <div 
        className="relative py-12 border-b border-zinc-800"
        style={{ 
          background: `linear-gradient(to bottom, ${league.color}20, transparent)` 
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {league.logo_url ? (
              <img 
                src={league.logo_url} 
                alt={league.name} 
                className="w-24 h-24 md:w-32 md:h-32 object-contain"
              />
            ) : (
              <div 
                className="w-24 h-24 md:w-32 md:h-32 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: league.color }}
              >
                <Trophy className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </div>
            )}
            <div className="text-center md:text-left">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase text-white tracking-wider">
                {league.name}
              </h1>
              <p className="text-lg text-zinc-400 mt-2">
                {league.sport} {league.season && `• ${league.season}`}
              </p>
              {league.description && (
                <p className="text-zinc-500 mt-3 max-w-2xl">{league.description}</p>
              )}
              <div className="flex items-center justify-center md:justify-start gap-6 mt-4 text-sm text-zinc-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {league.teams?.length || 0} Teams
                </span>
                {league.conferences?.length > 0 && (
                  <span>{league.conferences.length} Conferences</span>
                )}
                {league.divisions?.length > 0 && (
                  <span>{league.divisions.length} Divisions</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-30 bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto">
            {[
              { id: "home", label: "Home", icon: Home },
              { id: "standings", label: "Standings", icon: BarChart2 },
              { id: "teams", label: "Teams", icon: Users },
              { id: "schedule", label: "Schedule", icon: Calendar },
              { id: "results", label: "Results", icon: Trophy },
              ...(league.playoffs?.length > 0 ? [{ id: "playoffs", label: "Playoffs", icon: Shield }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? "text-orange-500 border-orange-500"
                    : "text-zinc-400 hover:text-white border-transparent"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              {activeTab === "home" && (
                <motion.div
                  key="home"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <LeagueHomeSection 
                    league={league}
                    standings={standingsWithTeams}
                    games={games}
                    teams={league.teams || []}
                    onTabChange={setActiveTab}
                  />
                </motion.div>
              )}

              {activeTab === "standings" && (
                <motion.div
                  key="standings"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <StandingsSection 
                    standings={standingsWithTeams}
                    conferences={league.conferences || []}
                    divisions={league.divisions || []}
                    leagueColor={league.color}
                    leagueSlug={league.slug}
                    leagueName={league.name}
                  />
                </motion.div>
              )}

              {activeTab === "teams" && (
                <motion.div
                  key="teams"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <TeamsSection 
                    teams={league.teams || []}
                    conferences={league.conferences || []}
                    divisions={league.divisions || []}
                    leagueColor={league.color}
                    leagueSlug={league.slug}
                    leagueName={league.name}
                  />
                </motion.div>
              )}

              {activeTab === "schedule" && (
                <motion.div
                  key="schedule"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <GamesSection 
                    games={upcomingGames} 
                    title="Upcoming Games"
                    emptyMessage="No upcoming games scheduled"
                    teams={league.teams}
                    conferences={league.conferences || []}
                    divisions={league.divisions || []}
                    leagueSlug={league.slug}
                    leagueName={league.name}
                  />
                </motion.div>
              )}

              {activeTab === "results" && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <GamesSection 
                    games={pastGames} 
                    title="Past Results"
                    emptyMessage="No past games"
                    teams={league.teams}
                    showScores
                    conferences={league.conferences || []}
                    divisions={league.divisions || []}
                    leagueSlug={league.slug}
                    leagueName={league.name}
                  />
                </motion.div>
              )}

              {activeTab === "playoffs" && (
                <motion.div
                  key="playoffs"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <PlayoffsSection 
                    playoffs={league.playoffs} 
                    teams={league.teams}
                    leagueColor={league.color}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Between Section Banners */}
            {betweenSectionBanners.length > 0 && (
              <div className="mt-8 space-y-4">
                {betweenSectionBanners.map(banner => (
                  <AdBanner key={banner.id} banner={banner} />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          {sidebarBanners.length > 0 && (
            <div className="lg:w-80 flex-shrink-0 space-y-4">
              {sidebarBanners.map(banner => (
                <AdBanner key={banner.id} banner={banner} variant="sidebar" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Ad Banner Component
const AdBanner = ({ banner, variant = "full" }) => {
  const content = (
    <img 
      src={banner.image_url} 
      alt={banner.alt_text || "Advertisement"} 
      className={`w-full rounded-lg object-cover ${
        variant === "sidebar" ? "max-h-[400px]" : "max-h-[200px]"
      }`}
    />
  );

  if (banner.link_url) {
    return (
      <a 
        href={banner.link_url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block hover:opacity-90 transition-opacity"
        data-testid="ad-banner"
      >
        {content}
      </a>
    );
  }

  return <div data-testid="ad-banner">{content}</div>;
};

export default LeaguePage;
